import React, { useState } from "react";

// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
  { value: "textarea", label: "Long Text" },
  { value: "select", label: "Dropdown" },
//   { value: "checkbox", label: "Checkbox" },
];

export default function FormBuilderApp({schema, setSchema}) {
  const [openSection, setOpenSection] = useState("builder"); // "builder" | "preview"

  // ─── HANDLERS ─────────────────────────────────────────────────────────────
  const addField = (e) => {
    e.preventDefault();
    setSchema([...schema, {
      id: crypto.randomUUID(),
      name: `field_${Date.now()}`,
      label: "New Field",
      type: "text",
      required: false,
      options: [],
    }]);
  };
  const updateField = (id, key, value) => {
    setSchema(schema.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const deleteField = (id) => {
    setSchema(schema.filter(f => f.id !== id));
  };

  return (
    <div style={styles.container}>

      <div style={styles.accordionItem}>
        <div 
          style={{...styles.accordionHeader, borderBottom: openSection === "builder" ? '1px solid #ddd' : 'none'}} 
          onClick={() => setOpenSection("builder")}
        >
          <div style={styles.headerTitle}>
            <span>{openSection === "builder" ? "▼" : "▶"}</span>
            <strong>1. Build Your Form</strong>
            <span style={styles.countBadge}>{schema.length} Fields</span>
          </div>
          <button onClick={addField} style={styles.btnPlus}>[+] Add Field</button>
        </div>

        {openSection === "builder" && (
          <div style={styles.accordionBody}>
            {schema.map((f, idx) => (
              <div key={f.id} style={styles.fieldRow}>
                <div style={styles.rowMeta}>
                  <span style={styles.idx}>#{idx + 1}</span>
                  <select 
                    value={f.type} 
                    disabled={f.type === "mobile"}
                    onChange={e => updateField(f.id, "type", e.target.value)}
                    style={styles.minimalSelect}
                  >
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label style={styles.reqLabel}>
                    <input 
                      disabled={f.type === "mobile"}
                      type="checkbox" 
                      checked={f.required} 
                      onChange={e => updateField(f.id, "required", e.target.checked)} 
                    /> Req.
                  </label>
                  <button disabled={f.type === "mobile"} onClick={() => deleteField(f.id)} style={styles.btnDel}>[X]</button>
                </div>

                <div style={styles.rowInputs}>
                  <input 
                    disabled={f.type === "mobile"}
                    style={styles.mainInp}
                    placeholder="Label"
                    value={f.label}
                    onChange={e => updateField(f.id, "label", e.target.value)}
                  />
                  <input 
                    style={styles.subInp}
                    placeholder="Property Name"
                    disabled={true}
                    value={f.name}
                    onChange={e => updateField(f.id, "name", e.target.value)}
                  />
                </div>

                {f.type === "select" && (
                  <input 
                    style={styles.optInp}
                    placeholder="Options (comma, separated)"
                    value={f.options?.map(o => o.label).join(",") || ""}
                    onChange={e => updateField(f.id, "options", e.target.value.split(",").map(v => ({ label: v.trim(), value: v.trim().toLowerCase() })))}
                  />
                )}
              </div>
            ))}
            {schema.length === 0 && <p style={styles.emptyText}>Click [+] to start.</p>}
          </div>
        )}
      </div>

      {/* ─── ACCORDION SECTION: PREVIEW ─── */}
      <div style={styles.accordionItem}>
        <div 
          style={styles.accordionHeader} 
          onClick={() => setOpenSection("preview")}
        >
          <div style={styles.headerTitle}>
            <span>{openSection === "preview" ? "▼" : "▶"}</span>
            <strong>2. View Live Form</strong>
          </div>
        </div>

        {openSection === "preview" && (
          <div style={styles.accordionBody}>
            <div style={styles.previewForm}>
              {schema.length === 0 && <p style={styles.emptyText}>Nothing to preview yet.</p>}
              {schema.map(f => (
                <div key={f.id} style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    {f.label || f.name} {f.required && <span style={{ color: 'red' }}>*</span>}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea style={styles.formInp} rows={2} />
                  ) : f.type === "select" ? (
                    <select style={styles.formInp}>
                      <option value="">Select an option</option>
                      {f.options.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="checkbox" /> <span style={{fontSize: '0.9rem'}}>Agree/Confirm</span>
                    </div>
                  ) : (
                    <input type={f.type} style={styles.formInp} />
                  )}
                </div>
              ))}
              {schema.length > 0 && <button style={styles.btnSubmit}>Submit (Demo)</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── STYLES ─────────────────────────────────────────────────────────────────
const styles = {
  container: { maxWidth: '600px', margin: '40px auto', fontFamily: 'sans-serif' },
  accordionItem: { border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px', background: '#fff' },
  accordionHeader: { 
    padding: '15px 20px', background: '#fcfcfc', cursor: 'pointer', 
    display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
  },
  headerTitle: { display: 'flex', alignItems: 'center', gap: '10px' },
  countBadge: { background: '#eee', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', color: '#666' },
  accordionBody: { padding: '20px', background: '#fff' },
  
  // Builder UI
  fieldRow: { padding: '15px', border: '1px solid #eee', borderRadius: '6px', marginBottom: '10px', background: '#fefefe' },
  rowMeta: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  idx: { color: '#999', fontSize: '0.8rem', fontWeight: 'bold' },
  minimalSelect: { fontSize: '0.8rem', padding: '2px' },
  reqLabel: { fontSize: '0.75rem', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '3px' },
  rowInputs: { display: 'flex', gap: '8px', marginBottom: '8px' },
  mainInp: { flex: 2, padding: '6px', border: '1px solid #ccc', borderRadius: '4px' },
  subInp: { flex: 1, padding: '6px', border: '1px solid #eee', borderRadius: '4px', fontSize: '0.8rem' },
  optInp: { width: '100%', padding: '6px', border: '1px solid #d0e1fd', borderRadius: '4px', background: '#f0f7ff', fontSize: '0.8rem', boxSizing: 'border-box' },
  
  // Buttons
  btnPlus: { background: '#4CAF50', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  btnDel: { background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer', fontWeight: 'bold' },
  btnSubmit: { width: '100%', padding: '10px', background: '#333', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' },

  // Preview Form
  previewForm: { display: 'flex', flexDirection: 'column', gap: '15px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '0.85rem', fontWeight: 'bold', color: '#444' },
  formInp: { padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', boxSizing: 'border-box' },
  emptyText: { textAlign: 'center', color: '#aaa', fontSize: '0.9rem' }
};